import type { EditorHost } from '@blocksuite/affine/block-std';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import { css, html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';

import { AIProvider } from '../provider';
import { extractSelectedContent } from '../utils/extract';

const PMID_RE = /\[PMID:\s*([0-9]+)\]/gi;
const DOI_RE = /\[DOI:\s*([^\]\s]+)\]/gi;
const ARXIV_RE = /\[arXiv:\s*([^\]\s]+)\]/gi;

type CitationToken = {
  type: 'pubmed' | 'doi' | 'arxiv';
  identifier: string;
};

function extractCitationTokens(text: string): CitationToken[] {
  if (!text) return [];
  const seen = new Set<string>();
  const out: CitationToken[] = [];
  const push = (type: CitationToken['type'], identifier: string) => {
    const key = `${type}:${identifier.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ type, identifier });
  };
  for (const m of text.matchAll(PMID_RE)) push('pubmed', m[1]);
  for (const m of text.matchAll(DOI_RE)) push('doi', m[1]);
  for (const m of text.matchAll(ARXIV_RE)) push('arxiv', m[1]);
  return out;
}

export class AskAIToolbarButton extends WithDisposable(LitElement) {
  static override styles = css`
    .ask-ai-button {
      border-radius: 4px;
      position: relative;
      user-select: none;
      cursor: pointer;
    }
  `;

  @property({ attribute: false })
  accessor host!: EditorHost;

  private readonly _onClick = () => {
    extractSelectedContent(this.host)
      .then(context => {
        if (!context) return;
        const probe = context.quote ?? context.markdown ?? '';
        const citationTokens = extractCitationTokens(probe);
        const sourceDocId: string | undefined =
          (this.host as any)?.doc?.id ?? (this.host as any)?.std?.store?.id;
        AIProvider.slots.requestQuoteToChat.next({
          context,
          host: this.host,
          focusInput: true,
          sourceDocId,
          citationTokens: citationTokens.length ? citationTokens : undefined,
        });
      })
      .catch(console.error);
  };

  override render() {
    return html`<div class="ask-ai-button" @click=${this._onClick}>
      <ask-ai-icon .size=${'middle'}></ask-ai-icon>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ask-ai-toolbar-button': AskAIToolbarButton;
  }
}
